import React, { useEffect, useRef } from 'react';
import './FlyingDots.css';

function FlyingDots() {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const particlesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const container = canvas.parentElement;
    
    // Set canvas size first
    const resizeCanvas = () => {
      const oldWidth = canvas.width;
      const oldHeight = canvas.height;
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
      
      // Scale particle positions if canvas was resized
      if (oldWidth > 0 && oldHeight > 0 && particlesRef.current.length > 0) {
        const scaleX = canvas.width / oldWidth;
        const scaleY = canvas.height / oldHeight;
        particlesRef.current.forEach(particle => {
          particle.baseX *= scaleX;
          particle.baseY *= scaleY;
          particle.x *= scaleX;
          particle.y *= scaleY;
        });
      }
    };
    
    // Initialize canvas size before creating particles
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Particle colors - mix of blue and other colors
    const colors = [
      '#4a90e2', // Primary blue
      '#6b9bd1', // Light blue
      '#7db3d3', // Sky blue
      '#9bb5d1', // Pale blue
      '#a8c5e0', // Very light blue
    ];

    // Create particles
    const numberOfParticles = 25;
    const particles = [];

    for (let i = 0; i < numberOfParticles; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.15,
        vy: (Math.random() - 0.5) * 0.15,
        radius: (2 + Math.random() * 1.5) * 0.8, // 20% smaller
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: 0.4 + Math.random() * 0.3,
        baseX: Math.random() * canvas.width,
        baseY: Math.random() * canvas.height,
        angle: Math.random() * Math.PI * 2,
        speed: 0.2 + Math.random() * 0.3,
      });
    }

    particlesRef.current = particles;

    // Connection distance threshold
    const connectionDistance = 150;

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      particles.forEach((particle, i) => {
        // Smooth organic movement - slower and farther
        particle.angle += 0.003;
        const time = Date.now() * 0.0005;
        particle.x = particle.baseX + Math.sin(particle.angle + time) * 70;
        particle.y = particle.baseY + Math.cos(particle.angle * 0.7 + time) * 50;
        
        // Repulsion from other particles to prevent clustering
        let repulsionX = 0;
        let repulsionY = 0;
        const repulsionDistance = 80;
        const repulsionStrength = 0.02;
        
        particles.forEach((other, j) => {
          if (i !== j) {
            const dx = particle.baseX - other.baseX;
            const dy = particle.baseY - other.baseY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance > 0 && distance < repulsionDistance) {
              // Push away from nearby particles
              const force = (1 - distance / repulsionDistance) * repulsionStrength;
              repulsionX += (dx / distance) * force;
              repulsionY += (dy / distance) * force;
            }
          }
        });
        
        // Apply repulsion forces
        particle.vx += repulsionX;
        particle.vy += repulsionY;
        
        // Add slight drift - allow particles to move freely off-screen
        particle.baseX += particle.vx;
        particle.baseY += particle.vy;
        
        // Gentle pull back towards center to keep dots on screen (only when very far)
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const maxDistance = Math.max(canvas.width, canvas.height) * 1.2;
        const dxToCenter = centerX - particle.baseX;
        const dyToCenter = centerY - particle.baseY;
        const distanceFromCenter = Math.sqrt(dxToCenter * dxToCenter + dyToCenter * dyToCenter);
        
        if (distanceFromCenter > maxDistance) {
          // Pull back towards center with gentle force
          const pullStrength = 0.008;
          particle.vx += (dxToCenter / distanceFromCenter) * pullStrength;
          particle.vy += (dyToCenter / distanceFromCenter) * pullStrength;
        }
        
        // Damping to prevent excessive speed
        particle.vx *= 0.99;
        particle.vy *= 0.99;

        // Draw connections to nearby particles (only if both are visible)
        const isParticleVisible = particle.x >= -50 && particle.x <= canvas.width + 50 && 
                                   particle.y >= -50 && particle.y <= canvas.height + 50;
        
        for (let j = i + 1; j < particles.length; j++) {
          const other = particles[j];
          const isOtherVisible = other.x >= -50 && other.x <= canvas.width + 50 && 
                                 other.y >= -50 && other.y <= canvas.height + 50;
          
          // Only draw connection if at least one particle is visible
          if (isParticleVisible || isOtherVisible) {
            const dx = particle.x - other.x;
            const dy = particle.y - other.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < connectionDistance) {
              // Draw connecting line
              const opacity = (1 - distance / connectionDistance) * 0.2;
              ctx.beginPath();
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(other.x, other.y);
              ctx.strokeStyle = `rgba(74, 144, 226, ${opacity})`;
              ctx.lineWidth = 0.5;
              ctx.stroke();
            }
          }
        }

        // Draw particle only if visible (with some margin for smooth entry/exit)
        if (isParticleVisible) {
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
          // Convert hex to rgba with opacity
          const hex = particle.color.replace('#', '');
          const r = parseInt(hex.substr(0, 2), 16);
          const g = parseInt(hex.substr(2, 2), 16);
          const b = parseInt(hex.substr(4, 2), 16);
          ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${particle.opacity})`;
          ctx.fill();
        }
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <div className="flying-dots-container">
      <canvas ref={canvasRef} className="flying-dots-canvas" />
    </div>
  );
}

export default FlyingDots;
